import { GameType } from '@daymade/common-lib/dbEnums';
import { TDrawTypeNumberSetup } from '@daymade/common-lib/dbJsonModels';
import { useEffect, useState } from 'react';
import { generateRandomLineNumbers } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';
import { lineContainsDuplicates } from 'utils/LottoEntryValidator';
import styles from './NumberSelectionBox.module.css';

interface INumberInputProps {
  bonus?: boolean;
  idx: number;
  upperBound: number;
  onChangeNumber: (idx: number, number: number | null, error?: string) => void;
  overrideNumber: number | null;
}

const NumberInput = ({
  idx,
  upperBound,
  onChangeNumber,
  overrideNumber,
  bonus,
}: INumberInputProps) => {
  const [number, setNumber] = useState<number | null>(overrideNumber);
  const [edited, setEdited] = useState(false);
  const [inputError, setInputError] = useState<string>();

  const formattedUpperBound = upperBound.toLocaleString();

  const validateNumber = () => {
    let error: string | undefined;

    if (!number) {
      error = `Enter a number between 1 and ${formattedUpperBound}`;
    } else if (number < 1 || number > upperBound) {
      error = `Your number must be between 1 and ${formattedUpperBound}`;
    }

    setInputError(error);
    return error;
  };

  const handleEditNumber = () => {
    if (edited) {
      const error = validateNumber();
      onChangeNumber(idx, number, error);
    }
  };

  useEffect(() => {
    handleEditNumber();
  }, [number, edited]);

  useEffect(() => {
    setNumber(overrideNumber);
  }, [overrideNumber]);

  return (
    <div
      className={`${styles.numberInput} ${
        edited && inputError ? styles.error : ''
      }`}
    >
      <input
        placeholder="-"
        className={`arcadeFont ${bonus ? styles.bonus : ''}`}
        type="number"
        min={1}
        max={upperBound}
        value={number ?? ''}
        onChange={(e) => {
          const newNumber =
            e.target.value === '' ? null : parseInt(e.target.value, 10);
          if (newNumber !== number) {
            setNumber(newNumber);
            if (!edited) setEdited(true);
          }
        }}
        onBlur={handleEditNumber}
        style={{ fontWeight: 'bold' }}
      />
    </div>
  );
};

interface INumberSelectionBoxProps {
  numberRanges: TDrawTypeNumberSetup;
  onEditNumbers: (
    newNumbers: {
      type: GameType.LOTTERY;
      main: (number | null)[];
      bonus: (number | null)[];
    },
    inputError?: string
  ) => void;
  previousEntriesByPlayer?: number[][];
}

const NumberSelectionBox = ({
  numberRanges,
  onEditNumbers,
  previousEntriesByPlayer = [],
}: INumberSelectionBoxProps) => {
  const [numbersMain, setNumbersMain] = useState<(number | null)[]>(
    numberRanges.main.map(() => null)
  );
  const [numbersBonus, setNumbersBonus] = useState<(number | null)[]>(
    numberRanges.bonus.map(() => null)
  );

  const [inputErrorMain, setInputErrorMain] = useState<string>();
  const [inputErrorBonus, setInputErrorBonus] = useState<string>();

  const singleNumberEntry = numberRanges.main.length === 1;
  const INCOMPLETE_ENTRY_ERROR = 'Incomplete entry';

  const validateNumbers = (nums: (number | null)[], bounds: number[]) => {
    const present = nums.filter((n) => n != null) as number[];
    let error: string | undefined;

    for (let i = 0; i < nums.length; i++) {
      const n = nums[i];
      const max = bounds[i];
      if (n != null && (n < 1 || n > max)) {
        error = `Number ${n} must be between 1 and ${max.toLocaleString()}`;
        return error;
      }
    }

    if (
      !singleNumberEntry &&
      present.length >= 2 &&
      lineContainsDuplicates(present)
    ) {
      error = `Your numbers contain duplicates! Please enter ${bounds.length} unique numbers.`;
      return error;
    }

    // 3) Incomplete
    if (present.length < bounds.length) {
      error = INCOMPLETE_ENTRY_ERROR;
      return error;
    }

    // 4) Repeat-draw check
    const already = previousEntriesByPlayer.some((prev) =>
      prev.every((n) => nums.includes(n))
    );
    if (already) {
      const list = present.join(', ');
      error = `You've already entered ${
        present.length > 1 ? 'these' : 'this'
      } number${present.length > 1 ? 's' : ''} (${list}). Please choose ${
        present.length > 1 ? 'different numbers' : 'a different number'
      }.`;
      return error;
    }

    return error;
  };

  useEffect(() => {
    setInputErrorMain(validateNumbers(numbersMain, numberRanges.main));
    setInputErrorBonus(validateNumbers(numbersBonus, numberRanges.bonus));
  }, [numbersMain, numbersBonus, previousEntriesByPlayer, numberRanges]);

  const dispatchChange = (
    type: GameType.LOTTERY,
    main: (number | null)[],
    bonus: (number | null)[]
  ) => {
    const mainErr = validateNumbers(main, numberRanges.main);
    const bonusErr = validateNumbers(bonus, numberRanges.bonus);
    const err = mainErr || bonusErr;
    onEditNumbers({ type, main, bonus }, err);
  };

  return (
    <div className={styles.minilottoNumberSelectionBox}>
      <div className={styles.numberSelectorContainer}>
        <div
          className={styles.randomButton}
          onClick={() => {
            const { main, bonus, type } = generateRandomLineNumbers(
              numberRanges.main,
              numberRanges.bonus
            );
            setNumbersMain(main);
            setNumbersBonus(bonus);
            dispatchChange(type, main, bonus);
          }}
        >
          <img
            className={styles.randomIcon}
            src={`${ASSET_PREFIX}/images/token-town/cherries-icon-white.svg`}
          />
        </div>

        {numberRanges.main.map((ub, idx) => (
          <NumberInput
            key={`main-${idx}`}
            idx={idx}
            upperBound={ub}
            overrideNumber={numbersMain[idx]}
            onChangeNumber={(i, val) => {
              const main = [...numbersMain];
              main[i] = val;
              setNumbersMain(main);
              dispatchChange(GameType.LOTTERY, main, numbersBonus);
            }}
          />
        ))}

        {numberRanges.bonus.map((ub, idx) => (
          <NumberInput
            key={`bonus-${idx}`}
            bonus
            idx={idx}
            upperBound={ub}
            overrideNumber={numbersBonus[idx]}
            onChangeNumber={(i, val) => {
              const bonus = [...numbersBonus];
              bonus[i] = val;
              setNumbersBonus(bonus);
              dispatchChange(GameType.LOTTERY, numbersMain, bonus);
            }}
          />
        ))}
      </div>

      {inputErrorMain && inputErrorMain !== INCOMPLETE_ENTRY_ERROR && (
        <p className={styles.lineError}>{inputErrorMain}</p>
      )}
      {inputErrorBonus && inputErrorBonus !== INCOMPLETE_ENTRY_ERROR && (
        <p className={styles.lineError}>{inputErrorBonus}</p>
      )}
    </div>
  );
};

export default NumberSelectionBox;
